<template>
  <!--
    A PEIXARIA, em papel (redesign 03/09 — o autor apontou: "mercado de peixes continua
    quebrado"). Dois defeitos morreram juntos: (1) o visual era o web antigo — caixas verdes e
    vermelhas, botões azuis, ItemCard escuro; (2) a lista de peixes ESTICAVA o NpcStage a
    milhares de pixels e o cenário do palco, em cover, virava pixels gigantes pretos — por isso
    o painel agora trava a MESMA altura das outras lojas (o clamp do LojaPainel) e a caixa de
    peixes rola por dentro. A cotação do dia é um quadro de recibo; cada peixe é um FishSlot
    com o preço e o Vender ao lado; o vender-tudo confirma num FishModal de papel.
  -->
  <div class="peixaria">
    <!-- A cotação do dia: o quadro que o Sr. Pedro consulta. -->
    <ul class="peixaria__cotacoes">
      <li>
        <span>{{ $t('shop.market.fishOfTheDay') }}</span>
        <strong v-if="marketInfo?.favoriteFish" class="peixaria__alta">
          {{ $t(`fishes.${marketInfo.favoriteFish.name}`) }} <em class="t-numero">200%</em>
        </strong>
        <strong v-else>{{ $t('shop.market.loading') }}</strong>
      </li>
      <li>
        <span>{{ $t('shop.market.leastWanted') }}</span>
        <strong v-if="marketInfo?.leastFavoriteFish">
          {{ $t(`fishes.${marketInfo.leastFavoriteFish.name}`) }} <em class="t-numero">30%</em>
        </strong>
        <strong v-else>{{ $t('shop.market.loading') }}</strong>
      </li>
      <li>
        <span>{{ $t('shop.market.hotRegion') }}</span>
        <strong class="peixaria__alta">{{ marketInfo?.favoriteMap ? $t(`shop.maps.${getMapKey(marketInfo.favoriteMap)}`) : $t('shop.market.loading') }}</strong>
      </li>
      <li>
        <span>{{ $t('shop.market.coldRegion') }}</span>
        <strong>{{ marketInfo?.leastFavoriteMap ? $t(`shop.maps.${getMapKey(marketInfo.leastFavoriteMap)}`) : $t('shop.market.loading') }}</strong>
      </li>
    </ul>

    <template v-if="playerFish.length > 0">
      <FishButton variante="primario" bloco som="compra" @click="handleSellAll">
        {{ $t('shop.market.sellAll') }} ·
        <span class="t-numero">{{ totalDaCaixa }}</span>
        <FishIcon nome="coins" tamanho="miudo" />
      </FishButton>

      <!-- A caixa de peixes ROLA por dentro — é o que impede o palco de esticar.
           `fish.id` é o id da linha de `player_fish` no V2 — a chave e o que a venda aceita. -->
      <ul class="peixaria__caixa">
        <li v-for="fish in playerFish" :key="fish.id" class="peixaria__peixe">
          <FishSlot
            :item="{ id: fish.id, nome: nomeDoPeixe(fish), imagem: fish.image, raridade: fish.rarity }"
            :interativo="false"
          />
          <div class="peixaria__quem">
            <p class="peixaria__nome">{{ nomeDoPeixe(fish) }}</p>
            <p class="peixaria__medida t-numero">
              {{ fish.weight }}kg · {{ fishPrices[fish.id] || 0 }} <FishIcon nome="coins" tamanho="miudo" />
            </p>
          </div>
          <FishButton tamanho="pequeno" @click="handleSellFish(fish)">
            {{ $t('shop.village.common.sell') }}
          </FishButton>
        </li>
      </ul>
    </template>

    <div v-else class="peixaria__vazia">
      <FishIcon nome="fish" tamanho="grande" />
      <p>{{ $t('shop.market.noFish') }}</p>
      <p class="peixaria__vazia-dica">{{ $t('shop.market.goFishing') }}</p>
    </div>

    <PurchaseConfirmModal
      :show="showConfirmModal"
      :item="selectedItem"
      :quantity="sellQuantity"
      :totalPrice="totalPrice"
      :is-sale="true"
      @confirm="confirmSell"
      @cancel="cancelSell"
    />

    <!-- Vender tudo: confirmação em papel (gasta a caixa inteira — confirmação legítima, D11). -->
    <FishModal
      :aberto="showSellAllModal"
      :titulo="$t('shop.market.sellAllConfirm')"
      :fechavel="!isSelling"
      @fechar="cancelSellAll"
    >
      <ul class="peixaria__resumo">
        <li>
          <span>{{ $t('shop.market.totalFish', { count: totalFishToSell }) }}</span>
        </li>
        <li>
          <span>{{ $t('purchase.modal.total') }}</span>
          <strong class="t-numero peixaria__alta">{{ totalSellPrice }} <FishIcon nome="coins" tamanho="miudo" /></strong>
        </li>
      </ul>
      <p class="peixaria__nota">{{ $t('business.fishMarket.bonusNote') }}</p>
      <template #acoes>
        <FishButton
          variante="primario"
          bloco
          som="compra"
          :carregando="isSelling"
          @click="confirmSellAll"
        >
          {{ $t('shop.market.sellAllConfirm') }}
        </FishButton>
        <FishButton variante="neutro" bloco :desabilitado="isSelling" @click="cancelSellAll">
          {{ $t('shop.village.common.close') }}
        </FishButton>
      </template>
    </FishModal>

    <!-- Alert/Notification component -->
    <GameAlert
      v-if="showAlert"
      :show="showAlert"
      :message="alertMessage"
      :type="alertType"
      @close="showAlert = false"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import PurchaseConfirmModal from '../components/PurchaseConfirmModal.vue';
import GameAlert from '@/shared/common/Alert.vue';
import { FishModal, FishButton, FishIcon, FishSlot } from '@/shared/ui';
import { falaDoLojista } from './falas';

/**
 * O mercado de peixe da vila, no contrato V2.
 *
 * O preço exibido é calculado **no cliente** — `fish.value` × o multiplicador
 * de `market/getFishPriceMultiplier` × a depreciação por idade — porque é só
 * vitrine. A venda de verdade manda apenas os **ids** para o servidor
 * (`player/sellFish` → `POST /sell`), que aplica a mesma fórmula com os bônus
 * todos; sumiram as rotas `calculate-fish-price`, `sell-all-fish` (que
 * aceitava um `totalPrice` dito pelo cliente!) e o fallback `day-now`.
 *
 * ## O Sr. Pedro era mudo
 *
 * As cinco outras lojas emitem `recado` e o lojista comenta no balão (5.2); esta era a única que
 * não emitia nada — o Sr. Pedro só dizia a saudação de entrada e depois calava, enquanto o jogador
 * vendia a caixa inteira. Agora ele comenta a cotação do peixe escolhido, a venda e a venda em
 * massa. O `GameAlert` continua onde estava: o balão é **cor**, o alerta é o **número** que o
 * servidor devolveu.
 */
export default {
  components: {
    PurchaseConfirmModal,
    GameAlert,
    FishModal,
    FishButton,
    FishIcon,
    FishSlot,
  },
  emits: ['interact', 'recado'],
  data() {
    return {
      /** A chave desta loja no i18n — de onde saem as falas do Sr. Pedro (`falas.js`). */
      tipo: 'market',
      showConfirmModal: false,
      selectedItem: null,
      sellQuantity: 1,
      totalPrice: 0,
      updateInterval: null,
      fishPrices: {}, // Add this to store calculated prices
      showSellAllModal: false,
      totalFishToSell: 0,
      totalSellPrice: 0,
      isSelling: false,
      showAlert: false,
      alertMessage: '',
      alertType: 'info', // 'success', 'error', 'warning', 'info'
    };
  },
  computed: {
    ...mapState('player', ['gold']),
    ...mapGetters('fishing', ['getMapName']), // Change to use getter instead of direct state
    ...mapGetters('market', ['getMarketInfo']),
    marketInfo() {
      const info = this.getMarketInfo || {
        favoriteFish: null,
        leastFavoriteFish: null,
        favoriteMap: null,
        leastFavoriteMap: null
      };

      // Adicionar verificação de tradução dos peixes
      if (info.favoriteFish) {
        info.favoriteFish = {
          ...info.favoriteFish,
          translatedName: this.$t(`fishes.${info.favoriteFish.name}`)
        };
      }
      if (info.leastFavoriteFish) {
        info.leastFavoriteFish = {
          ...info.leastFavoriteFish,
          translatedName: this.$t(`fishes.${info.leastFavoriteFish.name}`)
        };
      }

      return info;
    },
    playerFish() {
      return this.$store.state.player.inventory.fish || [];
    },
    /** A soma da vitrine — vai no botão de vender tudo. Estimativa; quem paga é o servidor. */
    totalDaCaixa() {
      return this.playerFish.reduce(
        (total, fish) => total + (this.fishPrices[fish.id] ?? 0),
        0
      );
    }
  },
  methods: {
    ...mapActions('player', ['sellFish']),
    ...mapActions('market', ['updateMarketPrices']),

    /** Cada fala é **sorteada** entre as do Sr. Pedro, e nunca a mesma duas vezes seguidas. */
    dizer(chave, params) {
      this.$emit('recado', falaDoLojista(this, this.tipo, chave, params));
    },

    /**
     * O nome do peixe como o `ItemCard` o mostra: em pt o nome do catálogo já **é** o nome exibido;
     * nos outros idiomas ele é a chave em `fishes.*`. Sem isto o Sr. Pedro falava "fishes.Tilápia"
     * em inglês.
     */
    nomeDoPeixe(peixe) {
      if (this.$i18n.locale === 'pt') return peixe.name;
      const traduzido = this.$t(`fishes.${peixe.name}`);
      return traduzido === `fishes.${peixe.name}` ? peixe.name : traduzido;
    },

    /**
     * O preço de vitrine de um peixe: valor base × multiplicador do mercado do
     * dia × depreciação por idade. É estimativa local — quem paga é o servidor,
     * na venda. A idade usa o relógio do **servidor** (`state.market.serverTime`,
     * que veio junto com o mercado) porque o relógio do jogador mente; sem
     * mercado carregado, cai para o relógio local em vez de zerar o preço.
     */
    getFishPrice(fish) {
      const baseValue = Number(fish.value || fish.baseValue || 0);
      if (isNaN(baseValue)) {
        return 0;
      }

      const multiplier =
        Number(this.$store.getters['market/getFishPriceMultiplier'](fish, this.$store.state)) || 1;
      let finalPrice = baseValue * multiplier;

      // Peixe velho vale menos — a geladeira premium congela a depreciação.
      if (!this.$store.state.player.isRefrigerator && fish.caughtAt) {
        const serverTime = this.$store.state.market.serverTime;
        const currentDate = serverTime ? new Date(serverTime) : new Date();
        const daysDifference = Math.floor(
          (currentDate - new Date(fish.caughtAt)) / (1000 * 60 * 60 * 24)
        );

        if (daysDifference >= 6) {
          finalPrice = 0;
        } else if (daysDifference >= 4) {
          finalPrice *= 0.2;
        } else if (daysDifference >= 1) {
          finalPrice *= 0.75;
        }
      }

      return isNaN(finalPrice) ? 0 : Math.floor(finalPrice);
    },

    calculateFishPrices() {
      const prices = {};
      for (const fish of this.playerFish) {
        prices[fish.id] = this.getFishPrice(fish);
      }
      this.fishPrices = prices;
    },

    getMapName(mapId) {
      return this.$store.getters['fishing/getMapName'](mapId);
    },
    async handleSellFish(data) {
      // Play sell sound
      await this.$store.dispatch('SoundController/playSound', { category: 'shop', type: 'sell' });

      const fishPrice = this.fishPrices[data.id] ?? this.getFishPrice(data);

      this.selectedItem = {
        ...data,
        price: fishPrice,
        value: fishPrice
      };
      this.totalPrice = fishPrice;
      this.sellQuantity = 1;
      this.showConfirmModal = true;

      this.dizer('about', { name: this.nomeDoPeixe(data) });
    },
    async confirmSell() {
      try {
        const playerId = this.$store.state.player.id;
        if (!playerId) {
          console.error('No player ID found');
          return;
        }

        // Só o **id** viaja — o preço é decidido no servidor. A action
        // `player/sellFish` já remove o peixe do inventário local e aplica o
        // ouro devolvido (`.gold`) via SET_GOLD; nenhum débito local extra.
        await this.sellFish({
          playerId,
          fish: [this.selectedItem.id]
        });

        this.dizer('sold', { name: this.nomeDoPeixe(this.selectedItem) });

        this.showConfirmModal = false;
        this.selectedItem = null;
        this.calculateFishPrices();
      } catch (error) {
        console.error('Failed to sell fish:', error);
        this.dizer('failed');
        this.showErrorAlert(
          error.response?.data?.message ||
            error.response?.data?.error?.message ||
            error.message
        );
      }
    },

    cancelSell() {
      this.showConfirmModal = false;
      this.selectedItem = null;
    },
    getMapKey(mapName) {
      const mapKeys = {
        'Píer': 'pier',
        'Praia': 'beach',
        'Lago': 'lake',
        'Rio Amazonas': 'amazon',
        'Mar Aberto': 'ocean',
        // English versions
        'Pier': 'pier',
        'Beach': 'beach',
        'Lake': 'lake',
        'Amazon River': 'amazon',
        'Open Sea': 'ocean'
      };
      
      return mapKeys[mapName] || mapName.toLowerCase();
    },
    handleSellAll() {
      // A soma dos preços de vitrine — estimativa; o modal já avisa que o
      // valor final pode variar com os bônus, aplicados no servidor.
      this.totalFishToSell = this.playerFish.length;
      this.totalSellPrice = this.playerFish.reduce(
        (total, fish) => total + (this.fishPrices[fish.id] ?? this.getFishPrice(fish)),
        0
      );
      this.showSellAllModal = true;
    },

    async confirmSellAll() {
      try {
        // Check if there are fish to sell
        if (this.totalFishToSell === 0) {
          this.showErrorAlert('No fish available to sell');
          this.showSellAllModal = false;
          return;
        }

        this.isSelling = true;
        const playerId = this.$store.state.player.id;

        // Vender tudo é vender **todos os ids** — a rota do V2 não aceita
        // `totalPrice` dito pelo cliente. `player/sellFish` remove os peixes
        // vendidos do inventário local e aplica `.gold` via SET_GOLD.
        const ids = this.playerFish.map((fish) => fish.id);
        const resultado = await this.sellFish({ playerId, fish: ids });

        // Play success sound
        await this.$store.dispatch('SoundController/playSound', { category: 'shop', type: 'sell' });

        // Show success notification
        this.showSuccessAlert(
          `Successfully sold ${resultado.sold} fish for ${resultado.goldGained} coins!`
        );

        this.dizer('soldAll', { count: resultado.sold, gold: resultado.goldGained });

        // Reset local fish list to reflect empty inventory
        this.resetFishList();
      } catch (error) {
        console.error('Failed to sell all fish:', error);
        this.dizer('failed');
        this.showErrorAlert(
          error.response?.data?.message ||
            error.response?.data?.error?.message ||
            error.message
        );
      } finally {
        this.isSelling = false;
        this.showSellAllModal = false;
      }
    },

    cancelSellAll() {
      this.showSellAllModal = false;
      this.totalFishToSell = 0;
      this.totalSellPrice = 0;
    },

    resetFishList() {
      this.totalFishToSell = 0;
      this.totalSellPrice = 0;

      // Recalculate from updated player inventory
      this.calculateFishPrices();
    },

    showSuccessAlert(message) {
      this.alertMessage = message;
      this.alertType = 'success';
      this.showAlert = true;
    },

    showErrorAlert(message) {
      this.alertMessage = message;
      this.alertType = 'error';
      this.showAlert = true;
    },
  },
  async created() {
    try {
      // Três cargas independentes, em paralelo: o mercado do dia, os efeitos
      // de evento e o bestiário — sem `allFishes` preenchido, o getter
      // `getFishPriceMultiplier` não descobre de qual mapa é cada peixe e o
      // bônus de região não aparece no preço.
      await Promise.all([
        this.updateMarketPrices(),
        this.$store.dispatch('market/calculateEventGoldMultiplier'),
        this.$store.dispatch('fishing/fetchAllFishes')
      ]);

      // Calculate initial fish prices once
      this.calculateFishPrices();
    } catch (error) {
      console.error('Failed to initialize market:', error);
    }
  },
  mounted() {
    // No need to continuously update prices - we'll calculate once when opened
    // Remove the interval that was previously recalculating prices every minute
  },
  beforeUnmount() {
    // Clear the interval if it exists (defensive cleanup)
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }
};
</script>

<style scoped>
.peixaria {
  display: flex;
  flex-direction: column;
  gap: var(--e-3);
  min-height: 0;
  /* O MESMO tamanho único das outras lojas (LojaPainel): o painel não cresce com a caixa —
   * era a caixa esticando o palco a milhares de pixels que quebrava a tela. */
  height: clamp(420px, 62vh, 640px);
  color: var(--tinta);
}

/* O quadro da cotação do dia: um recibo de quatro linhas — sem caixas verdes e vermelhas. */
.peixaria__cotacoes {
  list-style: none;
  margin: 0;
  padding: var(--e-2);
  background: var(--papel-frio);
  border: 2px solid var(--madeira);
  display: flex;
  flex-direction: column;
  gap: var(--e-1);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  flex-shrink: 0;
}

.peixaria__cotacoes li {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--e-3);
}

.peixaria__cotacoes strong {
  color: var(--tinta);
  text-align: right;
}

.peixaria__cotacoes em {
  font-style: normal;
  color: var(--tinta-fraca);
}

/* O que o mercado quer hoje fala em LATÃO — a única cor do quadro. */
.peixaria__alta {
  color: var(--latao) !important;
}

/* A caixa de peixes ROLA por dentro. */
.peixaria__caixa {
  list-style: none;
  margin: 0;
  padding: 3px; /* os aros de raridade desenham 2px para fora do encaixe */
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
}

.peixaria__peixe {
  display: flex;
  align-items: center;
  gap: var(--e-3);
  background: var(--papel-frio);
  border: 2px solid var(--madeira);
  padding: var(--e-2);
}

.peixaria__peixe :deep(.fish-slot) {
  width: 64px;
  flex-shrink: 0;
}

.peixaria__quem {
  flex: 1;
  min-width: 0;
}

.peixaria__nome {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.peixaria__medida {
  margin: 2px 0 0;
  display: inline-flex;
  align-items: center;
  gap: var(--e-1);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.peixaria__vazia {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--e-2);
  text-align: center;
  color: var(--tinta-fraca);
}

.peixaria__vazia p {
  margin: 0;
  font-size: var(--f-2);
}

.peixaria__vazia-dica {
  font-style: italic;
  font-size: var(--f-1);
}

/* O resumo do vender-tudo, dentro do modal de papel. */
.peixaria__resumo {
  list-style: none;
  margin: 0 0 var(--e-2);
  padding: var(--e-2) 0;
  border-top: 2px dashed var(--papel-sombra);
  border-bottom: 2px dashed var(--papel-sombra);
  display: flex;
  flex-direction: column;
  gap: var(--e-1);
  font-size: var(--f-2);
  color: var(--tinta-fraca);
}

.peixaria__resumo li {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--e-3);
}

.peixaria__resumo strong {
  display: inline-flex;
  align-items: center;
  gap: var(--e-1);
  font-size: var(--f-4);
}

.peixaria__nota {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}
</style>
